import React, { useEffect, useState } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeft, Printer } from 'lucide-react';
import { settingsService } from '../lib/services';

interface ReportStats {
  appointments: Record<string, number>;
  revenue: {
    collected: number;
    invoiced: number;
    pending: number;
    overdue: number;
    invoiceCount: number;
    byDepartment: Record<string, number>;
    byPaymentMethod: Record<string, number>;
    dailySeries: Array<{ date: string; amount: number }>;
  };
}

interface OrganizationSettings {
  name: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  tax_id: string;
  logo_url: string;
}

const ReportsPrint: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { stats, startDate, endDate } = location.state as { 
    stats: ReportStats; 
    startDate: string; 
    endDate: string;
  };

  const [orgSettings, setOrgSettings] = useState<OrganizationSettings>({
    name: 'Medical Center',
    address_line1: '123 Healthcare Avenue',
    address_line2: 'Suite 100',
    city: 'Medical City',
    state: 'State',
    postal_code: '12345',
    country: 'Country',
    phone: '+1 (555) 123-4567',
    email: 'info@medicalcenter.com',
    website: 'www.medicalcenter.com',
    tax_id: 'TAX-123456',
    logo_url: ''
  });

  useEffect(() => {
    loadOrganizationSettings();
  }, []);

  const loadOrganizationSettings = async () => {
    try {
      const settings = await settingsService.getAll();
      if (settings && settings.length > 0) {
        const orgData: any = {};
        settings.forEach((s: any) => {
          if (s.key.startsWith('org_')) {
            const key = s.key.replace('org_', '');
            orgData[key] = s.value;
          }
        });
        if (Object.keys(orgData).length > 0) {
          setOrgSettings(prev => ({ ...prev, ...orgData }));
        }
      }
    } catch (error) {
      console.error('Failed to load organization settings:', error);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-600 mb-4">No report data available</p>
          <button
            onClick={() => navigate('/reports')}
            className="text-primary-600 hover:text-primary-700"
          >
            Go back to Reports
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden bg-gray-100 border-b border-gray-300 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <button
          onClick={() => navigate('/reports')}
          className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Reports</span>
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center space-x-2 bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors duration-200"
        >
          <Printer className="w-5 h-5" />
          <span>Print Report</span>
        </button>
      </div>

      {/* Printable Report */}
      <div className="max-w-5xl mx-auto p-8 print:p-0">
        {/* Header */}
        <div className="border-b-4 border-primary-600 pb-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              {orgSettings.logo_url && (
                <img 
                  src={orgSettings.logo_url} 
                  alt="Organization Logo" 
                  className="h-16 mb-4"
                />
              )}
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{orgSettings.name}</h1>
              <div className="text-sm text-gray-600 space-y-1">
                <p>{orgSettings.address_line1}</p>
                {orgSettings.address_line2 && <p>{orgSettings.address_line2}</p>}
                <p>{orgSettings.city}, {orgSettings.state} {orgSettings.postal_code}</p>
                <p>{orgSettings.country}</p>
                <p className="mt-2">Phone: {orgSettings.phone}</p>
                <p>Email: {orgSettings.email}</p>
                <p>Website: {orgSettings.website}</p>
                {orgSettings.tax_id && <p>Tax ID: {orgSettings.tax_id}</p>}
              </div>
            </div>
            <div className="text-right">
              <h2 className="text-2xl font-bold text-primary-600 mb-2">Financial Report</h2>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Report Period:</strong></p>
                <p>{format(new Date(startDate), 'MMM dd, yyyy')} - {format(new Date(endDate), 'MMM dd, yyyy')}</p>
                <p className="mt-3"><strong>Generated:</strong></p>
                <p>{format(new Date(), 'MMM dd, yyyy HH:mm')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Executive Summary */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-gray-300 pb-2">Executive Summary</h3>
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-success-50 border border-success-200 rounded-lg p-4">
              <p className="text-sm font-medium text-success-700 mb-1">Collected Revenue</p>
              <p className="text-3xl font-bold text-success-900">${stats.revenue.collected.toFixed(2)}</p>
              <p className="text-xs text-success-600 mt-1">Actual payments received</p>
            </div>
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <p className="text-sm font-medium text-primary-700 mb-1">Invoiced Revenue</p>
              <p className="text-3xl font-bold text-primary-900">${stats.revenue.invoiced.toFixed(2)}</p>
              <p className="text-xs text-primary-600 mt-1">Total invoices issued</p>
            </div>
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
              <p className="text-sm font-medium text-warning-700 mb-1">Pending Revenue</p>
              <p className="text-3xl font-bold text-warning-900">${stats.revenue.pending.toFixed(2)}</p>
              <p className="text-xs text-warning-600 mt-1">Outstanding balance</p>
            </div>
            <div className="bg-error-50 border border-error-200 rounded-lg p-4">
              <p className="text-sm font-medium text-error-700 mb-1">Overdue Revenue</p>
              <p className="text-3xl font-bold text-error-900">${stats.revenue.overdue.toFixed(2)}</p>
              <p className="text-xs text-error-600 mt-1">Past due date</p>
            </div>
          </div>
        </div>

        {/* Key Performance Indicators */}
        <div className="mb-8 page-break-inside-avoid">
          <h3 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-gray-300 pb-2">Key Performance Indicators</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Metric</th>
                <th className="border border-gray-300 px-4 py-2 text-right font-semibold">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 px-4 py-2">Total Invoices Issued</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium">{stats.revenue.invoiceCount}</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 px-4 py-2">Average Invoice Amount</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium">
                  ${stats.revenue.invoiceCount > 0 ? (stats.revenue.invoiced / stats.revenue.invoiceCount).toFixed(2) : '0.00'}
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">Collection Rate</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium">
                  {stats.revenue.invoiced > 0 ? ((stats.revenue.collected / stats.revenue.invoiced) * 100).toFixed(1) : '0.0'}%
                </td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 px-4 py-2">Total Appointments</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium">
                  {Object.values(stats.appointments).reduce((sum, count) => sum + count, 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Revenue by Department */}
        <div className="mb-8 page-break-inside-avoid">
          <h3 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-gray-300 pb-2">Revenue by Department</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Department</th>
                <th className="border border-gray-300 px-4 py-2 text-right font-semibold">Revenue</th>
                <th className="border border-gray-300 px-4 py-2 text-right font-semibold">Percentage</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(stats.revenue.byDepartment).map(([dept, amount], index) => (
                <tr key={dept} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-300 px-4 py-2 capitalize">{dept}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right font-medium">${amount.toFixed(2)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">
                    {stats.revenue.invoiced > 0 ? ((amount / stats.revenue.invoiced) * 100).toFixed(1) : '0.0'}%
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-200 font-bold">
                <td className="border border-gray-300 px-4 py-2">Total</td>
                <td className="border border-gray-300 px-4 py-2 text-right">${stats.revenue.invoiced.toFixed(2)}</td>
                <td className="border border-gray-300 px-4 py-2 text-right">100.0%</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Revenue by Payment Method */}
        <div className="mb-8 page-break-inside-avoid">
          <h3 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-gray-300 pb-2">Revenue by Payment Method</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Payment Method</th>
                <th className="border border-gray-300 px-4 py-2 text-right font-semibold">Amount Collected</th>
                <th className="border border-gray-300 px-4 py-2 text-right font-semibold">Percentage</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(stats.revenue.byPaymentMethod).map(([method, amount], index) => (
                <tr key={method} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-300 px-4 py-2 capitalize">{method}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right font-medium">${amount.toFixed(2)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">
                    {stats.revenue.collected > 0 ? ((amount / stats.revenue.collected) * 100).toFixed(1) : '0.0'}%
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-200 font-bold">
                <td className="border border-gray-300 px-4 py-2">Total</td>
                <td className="border border-gray-300 px-4 py-2 text-right">${stats.revenue.collected.toFixed(2)}</td>
                <td className="border border-gray-300 px-4 py-2 text-right">100.0%</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Appointment Status Summary */}
        <div className="mb-8 page-break-inside-avoid">
          <h3 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-gray-300 pb-2">Appointment Status Summary</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Status</th>
                <th className="border border-gray-300 px-4 py-2 text-right font-semibold">Count</th>
                <th className="border border-gray-300 px-4 py-2 text-right font-semibold">Percentage</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(stats.appointments).map(([status, count], index) => {
                const total = Object.values(stats.appointments).reduce((sum, c) => sum + c, 0);
                return (
                  <tr key={status} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 px-4 py-2 capitalize">{status}</td>
                    <td className="border border-gray-300 px-4 py-2 text-right font-medium">{count}</td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      {total > 0 ? ((count / total) * 100).toFixed(1) : '0.0'}%
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-gray-200 font-bold">
                <td className="border border-gray-300 px-4 py-2">Total</td>
                <td className="border border-gray-300 px-4 py-2 text-right">
                  {Object.values(stats.appointments).reduce((sum, count) => sum + count, 0)}
                </td>
                <td className="border border-gray-300 px-4 py-2 text-right">100.0%</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t-2 border-gray-300 text-center text-sm text-gray-600">
          <p className="mb-2">
            <strong>Confidential - Internal Use Only</strong>
          </p>
          <p>
            This report contains confidential financial information. Unauthorized distribution is prohibited.
          </p>
          <p className="mt-2">
            {orgSettings.website} | {orgSettings.email} | {orgSettings.phone}
          </p>
          <p className="mt-4 text-xs text-gray-500">
            Page 1 of 1 | Generated on {format(new Date(), 'MMMM dd, yyyy')} at {format(new Date(), 'HH:mm')}
          </p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 1.5cm;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .page-break-inside-avoid {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          .print\\:p-0 {
            padding: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ReportsPrint;
